import { useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { snapPoint } from 'react-native-redash';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { DateRangePickerMobile } from '@components/DateRangePicker/DateRangePickerMobile';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { Box, ShadowBox, Text } from '@components/Restyle';
import { FormValues } from '@components/Tasks/TaskFormStack';
import theme from '@themes/theme';

export const EditTaskDatesBottomSheet: React.FC = () => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();

  // const {
  //   params: { fieldName, submitOnSelected },
  // } = route || ({} as any);

  const { values, setFieldValue, submitForm } = useFormikContext<FormValues>();

  const { startDate, dueDate } = values;

  const [height, setHeight] = useState(0);
  const translateY = useSharedValue(0);

  const [dueDateRange, setDueDateRange] = useState<DateRange>({
    start: startDate ? dayjs(startDate) : undefined,
    end: dueDate ? dayjs(dueDate) : undefined,
  } as DateRange);

  const close = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const pan = Gesture.Pan()
    .onUpdate(({ translationY }) => {
      if (translationY < 0) {
        translateY.value = translationY / 10;
      } else {
        translateY.value = translationY;
      }
    })
    .onEnd(({ translationY, velocityY }) => {
      const shouldClose =
        snapPoint(translationY, velocityY, [0, height]) === height;
      if (shouldClose) {
        runOnJS(close)();
      } else {
        translateY.value = withTiming(0);
      }
    });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  useEffect(() => {
    translateY.value = 0;
  }, []);

  return (
    <Box
      pt='m'
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
        justifyContent: 'flex-end',
        backgroundColor: 'transparent',
      }}>
      <Pressable
        style={[StyleSheet.absoluteFill, styles.backdrop]}
        onPress={close}
        accessibilityLabel='Close modal'
      />

      <GestureDetector gesture={pan}>
        <Animated.View style={animatedStyle}>
          <Box style={styles.belowModalWhiteSpace} />

          <ShadowBox
            variant='dark'
            borderTopLeftRadius='l'
            borderTopRightRadius='l'
            backgroundColor='white'
            paddingTop='m'
            paddingHorizontal='m'
            paddingVertical='l'
            style={{ backgroundColor: 'white' }}
            onLayout={(event) => {
              const { height } = event.nativeEvent.layout;
              setHeight(height);
            }}>
            <Box style={{ paddingBottom: insets.bottom }}>
              <Box style={styles.closeLine} />
              <Box
                flexDirection='row'
                marginTop='m'
                marginBottom='xs'
                justifyContent='space-between'>
                <Box flex={1}>
                  <Text variant='labelEmphasized' textAlign='center'>
                    'Select Dates'
                  </Text>
                </Box>
              </Box>

              <Box height={450}>
                <DateRangePickerMobile
                  onSelect={async (d1, d2) => {
                    const start = d1 ? dayjs(d1) : undefined;
                    const end = d2 ? dayjs(d2) : undefined;

                    setDueDateRange({ start, end } as DateRange);
                    await setFieldValue(
                      'startDate',
                      start?.toISOString() ?? null
                    );
                    await setFieldValue('dueDate', end?.toISOString() ?? null);
                    submitForm();
                    close();
                  }}
                  start={dueDateRange.start?.toDate()}
                  end={dueDateRange.end?.toDate()}
                />
              </Box>
            </Box>
          </ShadowBox>
        </Animated.View>
      </GestureDetector>
    </Box>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  closeLine: {
    alignSelf: 'center',
    width: 56,
    height: 4,
    borderRadius: 2.5,
    backgroundColor: theme.colors.grey03,
    marginBottom: theme.spacing.xs,
  },
  belowModalWhiteSpace: {
    ...StyleSheet.absoluteFillObject,
    top: '100%',
    height: theme.spacing.listFooter,
    backgroundColor: theme.colors.white,
    marginTop: -theme.spacing.xs,
    zIndex: 2,
  },
});
